import Button from "@app/components/button";
import ConfirmModal from "@app/components/confirm-modal";
import UpdateDeviceModal from "@app/components/device-modal";
import Input from "@app/components/input";
import Pagination from "@app/components/pagination";
import { Select } from "@app/components/select/select";
import { NormalSelectOptions } from "@app/components/select/select.type";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEFAULT_PAGE,
  DEFAULT_TOTAL_PAGES,
  PathnameUrl,
  Role,
  SystemMessage,
  USER_INFO_KEY,
} from "@app/constants";
import { DeviceStatus, DeviceType } from "@app/constants";
import DeviceService from "@app/services/http/device.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Device } from "@app/types";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useObservable from "@core/hooks/use-observable.hook";
import clsx from "clsx";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { debounceTime, Subject, Subscription } from "rxjs";
import { FaDotCircle } from "react-icons/fa";
import SocketService from "@app/services/socket.service";
import { useNavigate } from "react-router-dom";
import StorageService from "@core/services/storage";

function DeviceManagement() {
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [devices, setDevices] = useState<Device[]>([]);
  const listDevices = useRef<Device[]>();

  const [deviceName, setDeviceName] = useState("");
  const [buildingName, setBuildingName] = useState("");
  const [networkProviderName, setNetworkProvicerName] = useState("");
  const [phone, setPhone] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [status, setStatus] = useState<DeviceStatus>();
  const [type, setType] = useState<DeviceType>();

  // Lấy ngày hiện tại và định dạng YYYY-MM-DD
  const today = new Date().toISOString().split("T")[0];
  const [from_date] = useState(today);

  const navigate = useNavigate();

  const acc = StorageService.getObject(USER_INFO_KEY) as any;

  if (!acc) {
    navigate(PathnameUrl.LOGIN);
  }

  const filterStatusOptions: NormalSelectOptions = [
    {
      label: "Tất Cả",
      value: "",
    },
    {
      label: DeviceStatus.ACTIVE,
      value: DeviceStatus.ACTIVE,
    },
    {
      label: DeviceStatus.IN_ACTIVE,
      value: DeviceStatus.IN_ACTIVE,
    },
  ];

  const filterTypeOptions: NormalSelectOptions = [
    {
      label: "Tất Cả",
      value: "",
    },
    {
      label: DeviceType.DOWNLOAD,
      value: DeviceType.DOWNLOAD,
    },
    {
      label: DeviceType.CALL,
      value: DeviceType.CALL,
    },
    {
      label: DeviceType.ANSWER,
      value: DeviceType.ANSWER,
    },
  ];

  const deviceNameSubject = new Subject<string>();
  const buildingNameSubject = new Subject<string>();
  const networkProviderNameSubject = new Subject<string>();
  const phoneSubject = new Subject<string>();
  const deviceIdSubject = new Subject<string>();

  const [update, forceUpdate] = useForceUpdate();

  const { subscribeOnce, subscribeUntilDestroy } = useObservable();

  const isUpdate = (deviceId: string): boolean => {
    const isExit = listDevices.current?.find(
      (device) => device.device_id === deviceId
    );

    return !!isExit;
  };

  useEffect(() => {
    deviceNameSubject.pipe(debounceTime(300)).subscribe((data) => {
      setDeviceName(data);
    });
    buildingNameSubject.pipe(debounceTime(300)).subscribe((data) => {
      setBuildingName(data);
    });
    networkProviderNameSubject.pipe(debounceTime(300)).subscribe((data) => {
      setNetworkProvicerName(data);
    });
    phoneSubject.pipe(debounceTime(300)).subscribe((data) => {
      setPhone(data);
    });
    deviceIdSubject.pipe(debounceTime(300)).subscribe((data) => {
      setDeviceId(data);
    });
  }, [
    deviceNameSubject,
    buildingNameSubject,
    networkProviderNameSubject,
    phoneSubject,
    deviceIdSubject,
  ]);

  useEffect(() => {
    subscribeUntilDestroy(
      DeviceService.getDevices(
        page,
        status,
        type,
        deviceName,
        buildingName,
        networkProviderName,
        phone,
        deviceId,
        from_date
      ),
      (res) => {
        if (res.data.length) {
          setDevices(res.data);
          setTotalPages(res.total_page);
          listDevices.current = res.data;
        } else {
          setDevices([]);
          setTotalPages(DEFAULT_TOTAL_PAGES);
        }
      }
    );
  }, [
    update,
    page,
    status,
    type,
    deviceName,
    buildingName,
    networkProviderName,
    phone,
    deviceId,
  ]);

  //on socket
  //on message from server
  const subscription = new Subscription();

  //on
  useEffect(() => {
    //on join
    const subDeviceJoin = SocketService.onDeviceJoin().subscribe((deviceId) => {
      isUpdate(deviceId) && forceUpdate();
    });

    subscription.add(subDeviceJoin);

    //on disconnect
    const subDeviceDisconnect = SocketService.onDeviceDisconnect().subscribe(
      (deviceId) => {
        isUpdate(deviceId) && forceUpdate();
      }
    );

    subscription.add(subDeviceDisconnect);

    //on working
    const subDeviceWorking = SocketService.onDeviceWorking().subscribe(
      (deviceId) => {
        isUpdate(deviceId) && forceUpdate();
      }
    );

    subscription.add(subDeviceWorking);

    //on done
    const subDeviceDone = SocketService.onDeviceDone().subscribe((deviceId) => {
      isUpdate(deviceId) && forceUpdate();
    });

    subscription.add(subDeviceDone);
  }, [subscription]);

  useEffect(() => () => subscription.unsubscribe(), [subscription]);

  const handleDeleteDevice = (id: string) => {
    const deletePackageObs = openPortalDialog(ConfirmModal, {
      message:
        "Xóa Device sẽ mất hết dữ liệu download, call và answer. Bạn có chắc chắn xóa Device này không?",
    });

    deletePackageObs.afterClosed().subscribe((data) => {
      if (data?.isAccept) {
        subscribeOnce(DeviceService.deleteDevice(id), () => {
          addToast({ text: SystemMessage.DELETE_SUCCESS });
          forceUpdate();
        });
      }
    });
  };

  const handleUpdateDevice = (device: Device, isCompany?: boolean) => {
    const updateDeviceModalObs = openPortalDialog(UpdateDeviceModal, {
      device,
      isCompany
    });

    updateDeviceModalObs.afterClosed().subscribe((data) => {
      if (data?.device) {
        addToast({ text: SystemMessage.SUCCESS });
        forceUpdate();
      }
    });
  };

  const onPageChange = (page: number) => {
    setPage(page);
  };

  const handleFilterTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setType(e.target.value as any);
  };
  const handleFilterStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setStatus(e.target.value as any);
  };

  const onDeviceNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    deviceNameSubject.next(e.target.value);
  };
  const onBuildingNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    buildingNameSubject.next(e.target.value);
  };
  const onNetworkProviderChange = (e: ChangeEvent<HTMLInputElement>) => {
    networkProviderNameSubject.next(e.target.value);
  };
  const onPhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    phoneSubject.next(e.target.value);
  };
  const onDeviceIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    deviceIdSubject.next(e.target.value);
  };

  const viewLocation = (location: string) => {
    openPortalDialog(ConfirmModal, {
      message: location || "Chưa cập nhật vị trí",
    });
  };

  return (
    <div className="w-full mx-auto mt-4 pb-4">
      <div className="flex items-center justify-between pb-2 px-2 border-b border-[#409EFF]">
        <div className="flex items-center gap-2">
          <Input
            className="mt-0 max-w-[220px]"
            placeholder="Tên tòa nhà..."
            onChange={onBuildingNameChange}
          />
          <Input
            className="mt-0 max-w-[220px]"
            placeholder="Tên thiết bị..."
            onChange={onDeviceNameChange}
          />
          <Input
            className="mt-0 max-w-[220px]"
            placeholder="Nhà mạng..."
            onChange={onNetworkProviderChange}
          />
          <Input
            className="mt-0 max-w-[220px]"
            placeholder="SĐT..."
            onChange={onPhoneChange}
          />
          <Input
            className="mt-0 max-w-[220px]"
            placeholder="Emeil..."
            onChange={onDeviceIdChange}
          />
          <Select
            width={110}
            className="h-6 !mt-0"
            placeholder="Loại máy"
            options={filterTypeOptions}
            value={type}
            defaultValue=""
            onChange={handleFilterTypeChange}
          />
          <Select
            width={110}
            className="h-6 !mt-0"
            placeholder="Trạng thái"
            options={filterStatusOptions}
            value={status}
            defaultValue=""
            onChange={handleFilterStatusChange}
          />
        </div>
      </div>
      <div className="px-2 overflow-x-auto mt-2">
        <div className="inline-block min-w-full shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full leading-normal">
            <thead className="text-white">
              <tr className="border-b-2 border-gray-200 bg-[#409EFF] text-sm">
                <th className="p-3 text-left w-52">Online</th>
                <th className="p-3 text-left w-52">Hoạt động</th>
                <th className="p-3 text-left w-52">Vị trí</th>
                <th className="p-3 text-left w-52">Công ty</th>
                <th className="p-3 text-left w-72">Tên tòa nhà</th>
                <th className="p-3 text-left w-72">Tên thiết bị</th>
                <th className="p-3 text-left w-72">Nhà mạng</th>
                <th className="p-3 text-left w-72">Số điện thoại</th>
                <th className="p-3 text-left w-72">Emei Device</th>
                <th className="p-3 text-left w-72">Tổng GB/Erl</th>
                <th className="p-3 text-left w-52">Loại máy</th>
                {(type === DeviceType.CALL || !type) && (
                  <th className="p-3 text-left w-52">Thời gian gọi</th>
                )}
                {(type === DeviceType.DOWNLOAD || !type) && (
                  <th className="p-3 text-left w-52">Level</th>
                )}
                <th className="p-3 text-left w-52">Trạng Thái</th>
                <th className="p-3 text-left w-52">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {!!devices.length &&
                devices.map((item) => (
                  <tr
                    key={item._id}
                    className="border-b border-gray-200 text-sm hover:bg-[#51b83c2c]"
                  >
                    <td className="p-3">
                      {item.is_connecting ? (
                        <FaDotCircle className="text-green-500" />
                      ) : (
                        <FaDotCircle className="text-gray-500" />
                      )}
                    </td>
                    <td className="p-3">
                      {item.is_connecting && item.is_working ? (
                        <span className="text-green-500">Đang làm việc</span>
                      ) : (
                        <span className="text-red-500">Xong</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Button
                        label="Xem"
                        width="fit-content"
                        className="px-2 text-sm"
                        onClick={() => viewLocation(item.location)}
                      />
                    </td>
                    <td className="p-3">{item.company?.name || ""}</td>
                    <td className="p-3">{item.building_name}</td>
                    <td className="p-3">{item.device_name}</td>
                    <td className="p-3">{item.network_provider}</td>
                    <td className="p-3">{item.phone}</td>
                    <td className="p-3">{item.device_id}</td>
                    <td className="p-3">
                      {Number(item.total_today).toFixed(2)}
                    </td>
                    <td className="p-3">{item.type}</td>
                    {(type === DeviceType.CALL || !type) &&
                      (item.type === DeviceType.CALL ? (
                        <td className="p-3">
                          {item.min_time_call}m đến {item.max_time_call}m
                        </td>
                      ) : (
                        <td className="p-3">---</td>
                      ))}
                    {(type === DeviceType.DOWNLOAD || !type) &&
                      (item.type === DeviceType.DOWNLOAD ? (
                        <td className="p-3">{item.level}</td>
                      ) : (
                        <td className="p-3">---</td>
                      ))}
                    <td
                      className={clsx(
                        `p-3 ${
                          item.status === DeviceStatus.ACTIVE
                            ? "text-green-500"
                            : "text-red-500"
                        }`
                      )}
                    >
                      {item.status}
                    </td>

                    {acc?.role === Role.ADMIN && (
                      <td className="p-3 flex gap-1">
                        <Button
                          label="Xóa"
                          width="fit-content"
                          className="px-2 text-sm"
                          onClick={() => handleDeleteDevice(item._id)}
                        />
                        <Button
                          label="Sửa"
                          width="fit-content"
                          className="px-2 text-sm mt-2"
                          onClick={() => handleUpdateDevice(item)}
                        />
                        <Button
                          label="T.kê"
                          width="fit-content"
                          className="px-2 text-sm mt-2"
                          onClick={() =>
                            navigate(
                              PathnameUrl.STATISTICAL_MANAGEMENT +
                                "?_id=" +
                                item._id
                            )
                          }
                        />
                      </td>
                    )}

                    {/* đối với công ty  */}
                    {acc?.role === Role.COMPANY && (
                      <td className="p-3 flex gap-1">
                        <Button
                          label="Sửa"
                          width="fit-content"
                          className="px-2 text-sm mt-2"
                          onClick={() => handleUpdateDevice(item, true)}
                        />
                        <Button
                          label="T.kê"
                          width="fit-content"
                          className="px-2 text-sm mt-2"
                          onClick={() =>
                            navigate(
                              PathnameUrl.STATISTICAL_MANAGEMENT +
                                "?_id=" +
                                item._id
                            )
                          }
                        />
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
          {!devices.length && (
            <div className="flex justify-center">Không có dữ liệu</div>
          )}
        </div>
      </div>
      {totalPages > 0 && (
        <div className="flex justify-center mt-3">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}

export default DeviceManagement;
